import { useState, useMemo } from "react";
import "../../../../../../common/scss/table.scss";
import "./ImpersonationFilterTable.scss";
import LeafPanel from "../../../../../../common/leaf/LeafPanel/LeafPanel";
import { LeafIconButton } from "../../../../../../common/leaf/LeafIconButton/LeafIconButton";
import { FILTER_TYPE, IconFilter } from "../../../../../../common/icons/IconFilter/IconFilter";
import LeafBadgeCount from "../../../../../../common/leaf/LeafBadgeCount/LeafBadgeCount";
import { Button } from "../../../../../../common/components/Button/Button";

interface SearchItem {
  searchKey: string;
  displayValue: JSX.Element | string;
}

interface Column {
  label: string;
  accessor: string;
}

export interface CommonTableData {
  [key: string]: SearchItem;
}
type FilterTag = {
  filterKey: string;
  displayValue: JSX.Element | string;
};

type FilterMenuData = {
  accessor: string;
  filterTags: FilterTag[];
};

interface Props {
  data: CommonTableData[];
  header: Column[];
  filterMenuData: FilterMenuData;
  enableFilter?: boolean;
  enableSearch?: boolean;
  renderMobileCard?: (row: CommonTableData, index: number) => JSX.Element;
}

const addRemoveFilter = (activeFilters: string[], filter: string) => {
  return activeFilters.includes(filter) ? activeFilters.filter((f) => f !== filter) : [...activeFilters, filter];
};

const FilterMenu = ({
  show,
  onCloseClick,
  title,
  activeFilters,
  filterTags,
  setActiveFilters,
}: {
  show: boolean;
  onCloseClick: Function;
  title: string;
  activeFilters: string[];
  setActiveFilters: Function;
  filterTags: FilterTag[];
}) => {
  return (
    <LeafPanel id="" applyScroll={false} {...{ show, onCloseClick, title }}>
      <div className="filterMenu">
        {filterTags.map((filterTag) => (
          <Button
            onClick={() => setActiveFilters(addRemoveFilter(activeFilters, filterTag.filterKey))}
            className={`filterMenu__button ${
              activeFilters.includes(filterTag.filterKey) ? "filterMenu__button--active" : ""
            }`}
            key={filterTag.filterKey}
          >
            {filterTag.displayValue}
          </Button>
        ))}
      </div>
      <div className="filterMenu__footer">
        <Button onClick={() => setActiveFilters([])} className="filterMenu__footer__button">
          Clear Filters
        </Button>
        <Button onClick={() => onCloseClick()} className="filterMenu__footer__button">
          Close
        </Button>
      </div>
    </LeafPanel>
  );
};

const Table = ({
  data,
  header,
  filterMenuData,
  enableFilter = false,
  enableSearch = false,
  renderMobileCard,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showFilterMenu, setShowFilterMenu] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const sortedAndFilteredData = useMemo(() => {
    let filteredData = [...data];

    if (searchQuery) {
      filteredData = filteredData.filter((row) =>
        header.some((column) => {
          const item = row[column.accessor];
          return item?.searchKey?.toString().toLowerCase().includes(searchQuery.toLowerCase());
        }),
      );
    }

    if (activeFilters.length > 0) {
      filteredData = filteredData.filter((row) => {
        const filterAccessor = row[filterMenuData.accessor];

        return filterAccessor && activeFilters.includes(filterAccessor.searchKey);
      });
      filteredData.sort((a, b) => {
        const aValue = a[filterMenuData.accessor]?.searchKey;
        const bValue = b[filterMenuData.accessor]?.searchKey;
        return aValue.localeCompare(bValue);
      });
    }

    return filteredData;
  }, [data, header, searchQuery, activeFilters, filterMenuData]);

  return (
    <div>
      <div className="ImpersonationFilterTable__search-and-filter">
        {enableFilter && (
          <div className="ImpersonationFilterTable__filter">
            <LeafIconButton onClick={() => setShowFilterMenu(true)} Icon={<IconFilter type={FILTER_TYPE.ENABLED} />} />
            <span>
              <LeafBadgeCount count={activeFilters.length} hideZero={true} />
            </span>
          </div>
        )}

        {enableSearch && (
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ImpersonationFilterTable__searchInput"
            aria-label="Search"
          />
        )}
      </div>

      {renderMobileCard ? (
        <div className="ImpersonationFilterTable__mobileCards">
          {sortedAndFilteredData.length === 0 ? (
            <div className="ImpersonationFilterTable__noResults">No results found</div>
          ) : (
            sortedAndFilteredData.map((row, index) => renderMobileCard(row, index))
          )}
        </div>
      ) : (
        <div className="ImpersonationFilterTable__container">
          <table className="Table">
            <thead className="ImpersonationFilterTable__header">
              <tr>
                {header.map((column) => (
                  <th key={column.accessor}>{column.label}</th>
                ))}
              </tr>
            </thead>
          </table>
          <div className="ImpersonationFilterTable__bodyContainer">
            <table className="Table TableBody--rowBorder">
              <tbody className="TableBody--stripe">
                {sortedAndFilteredData.length === 0 ? (
                  <tr>
                    <td className="ImpersonationFilterTable__noResults" colSpan={header.length}>
                      No results found
                    </td>
                  </tr>
                ) : (
                  sortedAndFilteredData.map((row, index) => (
                    <tr key={index}>
                      {header.map((column) => (
                        <td key={column.accessor}>{row[column.accessor]?.displayValue}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <FilterMenu
        show={showFilterMenu}
        onCloseClick={() => setShowFilterMenu(false)}
        title="Filter by Tag"
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        filterTags={filterMenuData.filterTags}
      />
    </div>
  );
};

export default Table;
